/**
 * apps/automations-portal/src/features/dashboard/OverviewPage.tsx
 *
 * Client-facing dashboard: metric tiles, deployed block cards, and an
 * activity feed. Wired to the implemented client endpoints, with empty
 * states for a brand-new client with nothing deployed yet.
 */
import { useEffect, useState } from 'react';
import { Activity, Clock3, Sparkles } from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { clientApi, isNotImplemented } from '@/lib/api';
import type { Deployment, DeploymentStatus, OutcomesSummary } from '@/lib/types';

const DEPLOYMENT_STATUS_VARIANT: Record<DeploymentStatus, BadgeProps['variant']> = {
  ENABLED: 'success',
  DISABLED: 'secondary',
  DRAFT: 'secondary',
  DEPLOYING: 'warning',
  ERROR: 'destructive',
};

/**
 * What a CLIENT sees. Deliberately gentler than the admin labels: someone
 * looking at their own dashboard wants to know whether their automation is
 * working, not which internal state it is in.
 */
const DEPLOYMENT_STATUS_LABEL: Record<DeploymentStatus, string> = {
  ENABLED: 'Running',
  DISABLED: 'Paused',
  DRAFT: 'Being set up',
  DEPLOYING: 'Being set up',
  ERROR: 'Needs attention',
};

function MetricTile({ label, value, icon: Icon }: { label: string; value: string; icon: typeof Activity }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-5">
        <div className="rounded-md bg-accent p-2 text-accent-foreground">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-semibold leading-none">{value}</p>
          <p className="mt-1 text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function OverviewPage() {
  const [summary, setSummary] = useState<OutcomesSummary | null>(null);
  const [summaryUnavailable, setSummaryUnavailable] = useState(false);
  const [deployments, setDeployments] = useState<Deployment[] | null>(null);
  const [deploymentsUnavailable, setDeploymentsUnavailable] = useState(false);

  useEffect(() => {
    clientApi
      .outcomesSummary()
      .then(setSummary)
      .catch((err) => {
        if (isNotImplemented(err)) setSummaryUnavailable(true);
      });

    clientApi
      .deployments()
      .then(setDeployments)
      .catch((err) => {
        if (isNotImplemented(err)) setDeploymentsUnavailable(true);
      });
  }, []);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-8 p-8">
      <div>
        <h1 className="text-2xl font-semibold">Your automations</h1>
        <p className="text-sm text-muted-foreground">A snapshot of what the automation engine has done for you.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {summaryUnavailable ? (
          <Card className="sm:col-span-3">
            <CardContent className="py-6 text-center text-sm text-muted-foreground">
              Outcome reporting is coming soon.
            </CardContent>
          </Card>
        ) : summary ? (
          <>
            <MetricTile label="Runs this month" value={String(summary.runsThisMonth)} icon={Activity} />
            <MetricTile label="Hours saved" value={String(summary.hoursSaved)} icon={Clock3} />
            <MetricTile label="Active automations" value={String(summary.activeBlocks)} icon={Sparkles} />
          </>
        ) : (
          <>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </>
        )}
      </div>

      <div>
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Deployed automations
        </h2>

        {deploymentsUnavailable && (
          <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
            Your deployed automations will appear here once this view is switched on.
          </div>
        )}

        {!deploymentsUnavailable && !deployments && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {!deploymentsUnavailable && deployments?.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
            Nothing deployed yet. Your Hattie Automations contact will set up your first automation shortly.
          </div>
        )}

        {!deploymentsUnavailable && deployments && deployments.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {deployments.map((deployment) => (
              <Card key={deployment.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{deployment.blockName}</CardTitle>
                    <Badge variant={DEPLOYMENT_STATUS_VARIANT[deployment.status] ?? 'secondary'}>
                      {DEPLOYMENT_STATUS_LABEL[deployment.status] ?? deployment.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">
                    Live since {new Date(deployment.createdAt).toLocaleDateString('en-GB')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
